import { defineStore } from 'pinia'

export const useFilterOptionsStore = defineStore('FilterOptions', {
    state: () => ({
        keys: [
            'availabilityStatus',
            'competencies',
            'companySizes',
            'educationLevels',
            'industrySectors',
            'languages',
            'regions',
        ],
        availabilityStatus: [],
        competencies: [],
        companySizes: [],
        educationLevels: [],
        industrySectors: [],
        languages: [],
        regions: [],

        educationLevels_Flatten: [],
        languages_Flatten: [],
        regions_Flatten: [],

        profiles: [],
    }),
    getters: {
        getOptions: (state) => {
            return (key) => state[key];
        },
        getFlattenOptions: (state) => {
            return (key) => (Object.hasOwn(state, key + '_Flatten') ? state[key + '_Flatten'] : state[key]);
        },
        getAllOptions: (state) => {
            return () => {
                let options = {};
                state.keys.forEach((key) => options[key] = state[key]);

                return options;
            };
        },
        getAllFlattenOptions: (state) => {
            return () => {
                let options = {};
                state.keys.forEach((key) => {
                    options[key] = (Object.hasOwn(state, key + '_Flatten') ? state[key + '_Flatten'] : state[key]);
                });

                return options;
            };
        },
    },
    actions: {
        clearOptions(key) {
            this[key] = {};
        },
        clearAllOptions() {
            this.keys.forEach((key) => this.clearOptions(key));
        },
        setOptions(key, options) {
            if (Object.hasOwn(this, key + 'Hook')) {
                this[key + 'Hook'](options);
            }
        },
        setProfiles(options) {
            this.profiles = options;
        },

        // Hooks
        availabilityStatusHook(options) {
            options = this.convertDataset(options);

            this.availabilityStatus = options;
        },
        competenciesHook(options) {
            options = this.convertDataset(options);
            options = this.sortByHelper(options, 'value');

            this.competencies = options;
        },
        companySizesHook(options) {
            options = this.convertDataset(options);

            this.companySizes = options;
        },
        educationLevelsHook(options) {
            this.educationLevels = this.educationLevelsTransformFn(options);
            this.educationLevels_Flatten = this.flatGroupedOptions(this.educationLevels);
        },
        industrySectorsHook(options) {
            options = this.convertDataset(options);
            options = this.sortByHelper(options);

            this.industrySectors = options;
        },
        languagesHook(options) {
            this.languages = this.languagesTransformFn(options);
            this.languages_Flatten = this.flatGroupedOptions(this.languages);

        },
        regionsHook(options) {
            this.regions = this.regionsTransformFn(options);
            this.regions_Flatten = this.flatGroupedOptions(this.regions);
        },

        // Helpers
        convertDataset(dataset) {
            return Object.entries(dataset).map(([id, value]) => {
                return {
                    id: id,
                    value: value,
                };
            });
        },
        sortByHelper(dataset, index) {
            return dataset.sort((a, b) => {
                if (a[index] < b[index])
                    return -1;
                if (a[index] > b[index])
                    return 1;
                return 0;
            });
        },
        sortByDescHelper(dataset, index) {
            return dataset.sort((a, b) => {
                if (a[index] < b[index])
                    return 1;
                if (a[index] > b[index])
                    return -1;
                return 0;
            });
        },
        flatGroupedOptions(dataset) {
            return dataset.map((filterGroup) => {
                return filterGroup.options.map((option) => {
                    return {
                        id: option.id,
                        value: filterGroup.title + ' / ' + option.value,
                    };
                });
            }).flat();
        },

        // Transform functions
        educationLevelsTransformFn(options) {
            if (!options) {
                return [];
            }

            let educationFilters = [];
            Object.entries(options).forEach(([educationId, educationObj]) => {
                let options = this.convertDataset(educationObj.options);
                if (options.length) {
                    options = this.sortByHelper(options, 'value');

                    educationFilters.push({
                        filter: 'educationLevels',
                        title: educationObj.name,
                        maxLength: 6,
                        options: options,
                        hidden: false,
                        count: options.length,
                    });
                }
            });

            return educationFilters;
        },
        languagesTransformFn(options) {
            if (!options) {
                return [];
            }

            function filterUnique(value, index, array) {
                return array.indexOf(value) === index;
            }

            let languageSkillLevels = {
                // 1: this.$trans('noKnowledge'), -> don't display
                // 2: this.$trans('basicKnowledge'), -> don't display
                // 3: this.$trans('intermediate'), -> don't display
                4: this.$trans('advanced'),
                5: this.$trans('nearNative'),
                6: this.$trans('native'),
            };

            let activeLevels = Object.keys(languageSkillLevels);

            let activeLangLevels = this.profiles
                .map(profile => profile.filter_languages)
                .flat()
                .filter(langLevel => (activeLevels.indexOf(langLevel.split('-')[1]) > -1));

            let activeLangLevelsUnique = activeLangLevels.filter(filterUnique);

            let activeProfileLangs = activeLangLevels.map(langLevel => langLevel.split('-')[0]);

            languageSkillLevels = this.sortByDescHelper(this.convertDataset(languageSkillLevels), 'id');

            let languageFilters = [];
            Object.entries(options).forEach(([languageId, languageName]) => {
                let langCount = activeProfileLangs.filter(profileLang => profileLang == languageId).length;

                if (langCount) {
                    let options = languageSkillLevels
                        .map(level => {
                            return {
                                id: languageId + '-' + level.id,
                                value: level.value,
                            }
                        })
                        .filter((level) => activeLangLevelsUnique.indexOf(level.id) > -1);

                    languageFilters.push({
                        filter: 'languages',
                        title: languageName,
                        maxLength: -1,
                        options: options,
                        hidden: false,
                        count: langCount,
                    });
                }
            });

            languageFilters = languageFilters.sort((a, b) => {
                if (a['count'] < b['count'])
                    return 1;
                if (a['count'] > b['count'])
                    return -1;
                return 0;
            });

            return languageFilters;
        },
        regionsTransformFn(options) {
            if (!options) {
                return [];
            }

            let regionFilters = [];
            Object.entries(options).forEach(([regionsId, regionsObj]) => {
                let options = this.convertDataset(regionsObj.options);
                if (options.length) {
                    options = this.sortByHelper(options, 'value');

                    regionFilters.push({
                        filter: 'regions',
                        title: regionsObj.name,
                        maxLength: -1,
                        modifierSwitch: false,
                        modifierState: 'OR',
                        options: options,
                        selectAllSwitch: true,
                        hidden: false,
                        count: options.length,
                    });
                }
            });

            return regionFilters;
        }
    },
})
